import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, Rocket, TrendingUp, Users, Megaphone, Repeat, Target, DollarSign, Search } from 'lucide-react';

/**
 * Internal growth document for the product owner: feature roadmap + marketing & sales plan.
 * Content lives in structured data so the on-screen view, Markdown and PDF stay in sync.
 */

const TAG_LABEL = {
  revenue: { nl: '\u{1F4B0} Omzet', cls: 'bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30' },
  growth: { nl: '\u{1F501} Groei', cls: 'bg-[#48CAE4]/15 text-[#48CAE4] border-[#48CAE4]/30' },
  quickwin: { nl: '\u2B50 Quick win', cls: 'bg-[#FFB020]/15 text-[#FFB020] border-[#FFB020]/30' },
  needs_key: { nl: '\u{1F511} Sleutel nodig', cls: 'bg-[#FF6B35]/15 text-[#FF6B35] border-[#FF6B35]/30' },
};

const ROADMAP = [
  {
    category: 'Engagement & retentie', icon: Repeat, color: '#48CAE4',
    items: [
      { tags: ['growth', 'quickwin'], title: 'Voortgangs-dashboard met trends', desc: 'Scoreverloop per skill over tijd (data is er al).' },
      { tags: ['growth'], title: 'Doelen, streaks & challenges', desc: 'Gamification: "3 sessies deze week" \u2192 terugkerende gebruikers.' },
      { tags: ['growth'], title: 'Achievements/badges & spot-leaderboards', desc: 'Sociale prikkel en competitie per spot/skill.' },
      { tags: ['quickwin'], title: 'Sessie-logboek', desc: 'Auto uit Strava + handmatig \u2014 je kite-dagboek.' },
      { tags: ['growth', 'needs_key'], title: 'Wind-alerts (push/e-mail)', desc: '"Goede condities op jouw spot morgen" \u2192 reden om terug te komen.' },
      { tags: [], title: 'AI-trainingsplan', desc: 'Persoonlijk oefenplan op basis van je zwaktes.' },
      { tags: [], title: 'Auto-highlightclip', desc: 'Deelbare clip uit je upload = gratis marketing.' },
    ],
  },
  {
    category: 'Monetisatie', icon: DollarSign, color: '#2EE59D',
    items: [
      { tags: ['revenue', 'needs_key'], title: 'Mollie live (P0)', desc: 'Zonder betalen geen omzet \u2014 API-sleutel nog nodig.' },
      { tags: ['revenue', 'growth'], title: 'Referral-programma', desc: 'Gratis credits per aangebrachte vriend \u2014 goedkoopste groei.' },
      { tags: ['revenue'], title: 'Kiteschool / team-accounts (B2B)', desc: 'Scholen beheren leerlingen + bulk-credits \u2014 hoogste omzet per klant.' },
      { tags: ['revenue', 'needs_key'], title: 'Gear-affiliate', desc: 'Gratis Gear Advisor linkt naar shops \u2192 commissie op verkoop.' },
      { tags: ['revenue'], title: 'Cadeaubonnen & kortingscodes', desc: 'Extra conversie- en cadeau-omzet.' },
    ],
  },
  {
    category: 'Groei & SEO', icon: Search, color: '#FFB020',
    items: [
      { tags: ['growth'], title: 'Publieke spot-pagina\u2019s (SEO)', desc: 'Elke spot met live wind + forecast \u2192 Google-verkeer.' },
      { tags: ['growth'], title: 'Publieke deelbare analyses', desc: 'Share-token bestaat al \u2192 virale reach.' },
      { tags: [], title: 'Contenthub/blog', desc: 'Technieken & spot-guides voor SEO.' },
      { tags: [], title: 'Embeddable wind-widget', desc: 'Voor kiteclubs \u2192 backlinks + naamsbekendheid.' },
    ],
  },
  {
    category: 'Backend / infra', icon: FileText, color: '#FF6B35',
    items: [
      { tags: ['quickwin', 'needs_key'], title: 'E-mailservice (transactioneel)', desc: 'Welkom, bonnetjes, wachtwoord-reset, alerts \u2014 fundament voor veel.' },
      { tags: [], title: 'Rate-limiting Gear Advisor', desc: 'Misbruikbescherming = kostenbeheersing.' },
      { tags: [], title: 'Video-opslag lifecycle', desc: 'Oude video\u2019s auto-verwijderen \u2192 AI/opslagkosten laag.' },
      { tags: [], title: 'GDPR data-export & verwijderen', desc: 'EU-verplicht.' },
      { tags: ['needs_key'], title: 'Product-analytics (PostHog)', desc: 'Meet je funnel echt.' },
      { tags: ['growth'], title: 'DE/FR/ES vertalingen', desc: 'Vergroot de markt fors (Duitsland = grote kite-markt).' },
    ],
  },
];

const MARKETING = [
  { icon: Target, heading: '1. Positionering', lines: [
    '"Jouw AI-kitecoach. 24/7. In jouw beelden." \u2014 persoonlijke coaching tegen een fractie van de prijs van een instructeur.',
    'Kernbelofte: sneller beter kiten, objectief, na elke sessie.',
  ] },
  { icon: Users, heading: '2. Doelgroepen (ICP)', lines: [
    'B2C primair: beginner\u2192intermediate kiters (25\u201345 jr) die plateauen en sneller willen vorderen.',
    'B2C secundair: gevorderden die tricks/big air verfijnen.',
    'B2B (hoogste waarde): kitescholen & -clubs die hun lessen versterken met AI-feedback.',
  ] },
  { icon: DollarSign, heading: '3. Pricing', lines: [
    'Free als acquisitie (2 credits + gratis Gear Advisor = de haak).',
    'Jaarabonnement met ~2 maanden korting (cashflow + lagere churn).',
    'B2B-staffel: per-leerling of bundels credits voor scholen.',
  ] },
  { icon: Megaphone, heading: '4. Acquisitiekanalen (geprioriteerd)', lines: [
    '1) Community-first: kitesurf-Facebookgroepen, Reddit r/kitesurfing, lokale clubs \u2014 deel echte AI-analyses.',
    '2) Influencer/ambassadeurs: 5\u201310 creators gratis Elite + affiliate-code.',
    '3) SEO: spot-pagina\u2019s + spot-guides \u2192 organisch verkeer.',
    '4) Short-form video (Reels/TikTok/Shorts): "AI roast mijn kite-jump".',
    '5) Partnerships met merken/shops (affiliate).',
    '6) Paid ads pas later, als de funnel bewezen is.',
  ] },
  { icon: Repeat, heading: '5. Virale/groei-loops', lines: [
    'Elke gedeelde analyse bevat KITEIQ-branding \u2192 nieuwe bezoekers.',
    'Referral: "Geef een vriend 2 gratis analyses, krijg er zelf 2".',
    'Gratis Gear Advisor = top-of-funnel zonder login \u2192 e-mail capture \u2192 nurture naar Pro.',
  ] },
  { icon: TrendingUp, heading: '6. Retentie & lifecycle (e-mail)', lines: [
    'Welkomstflow \u2192 eerste analyse pushen (aha-moment).',
    'Wind-alerts ("goede condities") \u2192 terugkeer.',
    'Maandelijkse "je voortgang"-digest.',
    'Win-back voor slapende gebruikers (engagement-tracking zit al in admin).',
  ] },
  { icon: Users, heading: '7. Sales (B2B)', lines: [
    'Korte demo-flow voor scholen: 1 gratis schoolaccount, dan staffelprijs.',
    'Outreach naar kitescholen (Tarifa, NL/BE-kust, Egypte, Brazili\u00eb).',
    'Case study met 1 enthousiaste school als social proof.',
  ] },
  { icon: Rocket, heading: '8. Gefaseerde lancering', lines: [
    'Fase 1 (nu): Mollie live + e-mail + referral \u2192 soft launch in communities.',
    'Fase 2: SEO spot-pagina\u2019s + influencers + content.',
    'Fase 3: B2B-scholen + betaalde ads schalen.',
  ] },
  { icon: Target, heading: '9. KPI\u2019s', lines: [
    'Activatie (% dat 1e analyse doet) \u00b7 Free\u2192Pro conversie \u00b7 MRR/churn (zit in admin) \u00b7 CAC vs LTV \u00b7 virale co\u00ebffici\u00ebnt.',
  ] },
];

const RECOMMENDED = [
  'Mollie live (zonder betalen geen inkomsten)',
  'E-mailservice (fundament voor lifecycle/alerts/bonnetjes)',
  'Referral-programma (goedkoopste groei)',
  'Publieke spot-pagina\u2019s (gratis SEO-verkeer)',
  'Gear-affiliate (passief inkomen uit je gratis tool)',
];

function buildMarkdown() {
  let md = '# KITEIQ \u2014 Groeiplan (Marketing, Sales & Roadmap)\n\n';
  md += `_Gegenereerd: ${new Date().toLocaleDateString('nl-NL')}_\n\n`;
  md += '## Feature-roadmap\n\n';
  for (const cat of ROADMAP) {
    md += `### ${cat.category}\n\n`;
    for (const it of cat.items) {
      const tags = it.tags.map((tg) => TAG_LABEL[tg]?.nl || tg).join(' ');
      md += `- **${it.title}**${tags ? ` _(${tags})_` : ''} \u2014 ${it.desc}\n`;
    }
    md += '\n';
  }
  md += '## Marketing- & salesplan\n\n';
  for (const sec of MARKETING) {
    md += `### ${sec.heading}\n\n`;
    for (const line of sec.lines) md += `- ${line}\n`;
    md += '\n';
  }
  md += '## Aanbevolen bouwvolgorde (omzet-kritisch)\n\n';
  RECOMMENDED.forEach((r, i) => { md += `${i + 1}. ${r}\n`; });
  return md;
}

export const GrowthPlan = () => {
  const { t } = useTranslation();

  const downloadMarkdown = () => {
    const blob = new Blob([buildMarkdown()], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `kiteiq-groeiplan-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = () => {
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    let body = `<h1>KITEIQ \u2014 Groeiplan</h1><p class="meta">Gegenereerd: ${new Date().toLocaleDateString('nl-NL')}</p>`;
    body += '<h2>Feature-roadmap</h2>';
    for (const cat of ROADMAP) {
      body += `<h3>${esc(cat.category)}</h3><ul>`;
      for (const it of cat.items) {
        const tags = it.tags.map((tg) => esc(TAG_LABEL[tg]?.nl || tg)).join(' ');
        body += `<li><strong>${esc(it.title)}</strong>${tags ? ` <em>(${tags})</em>` : ''} \u2014 ${esc(it.desc)}</li>`;
      }
      body += '</ul>';
    }
    body += '<h2>Marketing- &amp; salesplan</h2>';
    for (const sec of MARKETING) {
      body += `<h3>${esc(sec.heading)}</h3><ul>`;
      for (const line of sec.lines) body += `<li>${esc(line)}</li>`;
      body += '</ul>';
    }
    body += '<h2>Aanbevolen bouwvolgorde</h2><ol>';
    RECOMMENDED.forEach((r) => { body += `<li>${esc(r)}</li>`; });
    body += '</ol>';
    const html = `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>KITEIQ Groeiplan</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#111;max-width:820px;margin:32px auto;padding:0 24px;line-height:1.5}
      h1{color:#00708a;margin-bottom:4px}h2{color:#00708a;border-bottom:2px solid #e6e6e6;padding-bottom:6px;margin-top:28px}
      h3{color:#222;margin-top:18px}.meta{color:#888;font-size:13px}ul,ol{margin:6px 0 14px}li{margin:4px 0}em{color:#a05a00;font-style:normal}</style>
      </head><body>${body}</body></html>`;
    // Render into a hidden iframe via srcdoc (no document.write, no popup blocker).
    // Content is fully static + HTML-escaped above, so srcdoc is safe.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    iframe.srcdoc = html;
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Print failed', e);
      }
      setTimeout(() => iframe.remove(), 1000);
    };
    document.body.appendChild(iframe);
  };

  return (
    <div data-testid="admin-growth-plan">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <div className="label-eyebrow text-[#2EE59D] flex items-center gap-2"><Rocket className="w-4 h-4" /> {t('admin.growth.eyebrow')}</div>
          <h2 className="font-display text-2xl mt-1">{t('admin.growth.title')}</h2>
          <p className="text-muted-foreground text-sm mt-1">{t('admin.growth.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={downloadMarkdown} className="bg-transparent border-white/15 hover:bg-white/10 rounded-xl" data-testid="growth-download-md">
            <Download className="w-4 h-4 mr-2" /> Markdown
          </Button>
          <Button onClick={downloadPdf} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="growth-download-pdf">
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Recommended build order */}
      <div className="kite-card p-5 mb-6 border-[#2EE59D]/25">
        <div className="label-eyebrow text-muted-foreground mb-3">{t('admin.growth.recommended')}</div>
        <ol className="space-y-2">
          {RECOMMENDED.map((r, i) => (
            <li key={r} className="flex items-center gap-3 text-sm">
              <span className="w-6 h-6 rounded-full bg-[#2EE59D]/15 text-[#2EE59D] text-xs font-data font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
              {r}
            </li>
          ))}
        </ol>
      </div>

      {/* Feature roadmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ROADMAP.map((cat) => {
          const Icon = cat.icon;
          return (
            <div key={cat.category} className="kite-card p-5">
              <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4" style={{ color: cat.color }} /><span className="label-eyebrow text-muted-foreground">{cat.category}</span></div>
              <div className="space-y-3">
                {cat.items.map((it) => (
                  <div key={it.title} className="rounded-xl bg-[#12161B] border border-white/10 p-3">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="font-medium text-sm">{it.title}</div>
                      <div className="flex items-center gap-1 flex-wrap">
                        {it.tags.map((tg) => (
                          <Badge key={tg} variant="outline" className={`${TAG_LABEL[tg]?.cls || ''} text-[10px]`}>{TAG_LABEL[tg]?.nl || tg}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{it.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Marketing & sales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {MARKETING.map((sec) => {
          const Icon = sec.icon;
          return (
            <div key={sec.heading} className="kite-card p-5">
              <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#48CAE4]" /><span className="font-medium">{sec.heading}</span></div>
              <ul className="space-y-1.5">
                {sec.lines.map((line, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex gap-2"><span className="text-[#00B4D8] mt-1.5 w-1 h-1 rounded-full bg-[#00B4D8] shrink-0" />{line}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};
